import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store, Action } from '@ngrx/store';

import * as fromCustomer from './customer.reducer';
import * as CustomerSelectors from './customer.selectors';
import * as CustomerActions from './customer.actions';
import {
  LoadType,
  UpdateAppointmentDTO,
  UpdateCustomerDTO,
  NewCustomerDTO,
  DrawerType,
  ACTIVITY_TYPES,
  CreateActivityDTO,
  ILead
} from '@care-portals/api-interfaces';
import { ADMIN_WEB_PAGES_URLS } from '@care-portals/data';
import { buildOrgAwareUrl } from '../app/layout-org.util';
import { DrawerStateFacade } from '../drawer-state/drawer-state.facade';
import { APIService } from '../../services/api/api.service';

@Injectable()
export class CustomerFacade {
  loading$ = this.store.pipe(select(CustomerSelectors.getCustomersLoading));
  saving$ = this.store.pipe(select(CustomerSelectors.getCustomerSaving));
  error$ = this.store.pipe(select(CustomerSelectors.getCustomersError));
  customers$ = this.store.pipe(select(CustomerSelectors.getCustomerEntities));
  paymentMethods$ = this.store.pipe(
    select(CustomerSelectors.getPaymentMethods)
  );
  paymentMethodsLoading$ = this.store.pipe(
    select(CustomerSelectors.getPaymentMethodsLoading)
  );
  selectedCustomer$ = this.store.pipe(
    select(CustomerSelectors.selectCurrentCustomer)
  );
  selectedCustomerResources$ = this.store.pipe(
    select(CustomerSelectors.selectSelectedCustomerResources)
  );
  selectedCustomerCredit$ = this.store.pipe(
    select(CustomerSelectors.selectSelectedCustomerCredit)
  );
  createDrawer$ = this.store.pipe(select(CustomerSelectors.selectCreateDrawer));
  activities$ = this.store.pipe(
    select(CustomerSelectors.getCustomerActivities)
  );
  /* ------------------------------------------------------------------------ */
  /*                       Customer Appointments Selectors                    */
  /* ------------------------------------------------------------------------ */
  customerAppointments$ = this.store.pipe(
    select(CustomerSelectors.getCustomerAppointments)
  );
  customerAppointmentsLoading$ = this.store.pipe(
    select(CustomerSelectors.getCustomerAppointmentsLoading)
  );

  constructor(
    private readonly store: Store<fromCustomer.CustomerPartialState>,
    private readonly drawerFacade: DrawerStateFacade,
    private readonly apiService: APIService,
    public readonly router: Router
  ) {}

  dispatch(action: Action) {
    this.store.dispatch(action);
  }

  loadCustomers(loadType: LoadType, filters: any, reset: boolean = false) {
    if (reset) {
      this.dispatch(CustomerActions.resetCustomers());
    }
    this.dispatch(CustomerActions.loadCustomers({ loadType, filters }));
  }

  loadCustomer(id: string) {
    this.dispatch(CustomerActions.loadCustomer({ id }));
  }

  loadCustomerLead(id: string) {
    this.dispatch(CustomerActions.loadCustomerLead({ id }));
  }

  loadCustomerActivities(customerId: string) {
    this.dispatch(CustomerActions.loadCustomerActivities({ customerId }));
  }

  updateCustomer(id: string, data: UpdateCustomerDTO) {
    this.dispatch(CustomerActions.updateCustomer({ id, data }));
  }

  updateCustomerExtras(id: string, extras: any) {
    this.dispatch(CustomerActions.updateCustomerExtras({ id, extras }));
  }

  getCustomerUrl(id: string) {
    return buildOrgAwareUrl(
      `/${ADMIN_WEB_PAGES_URLS.customers.byId.replace(':id', id)}`
    );
  }

  openCustomer(id: string) {
    this.router.navigateByUrl(this.getCustomerUrl(id));
  }
  openCustomerByBigcommerceId(bigcommerceId: string) {
    this.dispatch(
      CustomerActions.openCustomerByBigcommerceId({ bigcommerceId })
    );
  }

  updateWatchlist(isAdd: boolean, userId?: string) {
    this.dispatch(CustomerActions.updateWatchlist({ isAdd, userId }));
  }

  openOscar(emrId: string) {
    window.open(
      `https://cover-health.kai-oscar.com/oscar/demographic/demographiccontrol.jsp?displaymode=edit&dboperation=search_detail&demographic_no=${emrId}`,
      '_blank'
    );
  }

  openBigcommerce(id: string) {
    // TODO need to make the store dynamic based on the org
    window.open(
      `https://store-dqnrz0mrxr.mybigcommerce.com/manage/customers?idFrom=${id}&idTo=${id} `,
      '_blank'
    );
  }

  openStripe(id: string) {
    window.open(`https://dashboard.stripe.com/customers/${id}`, '_blank');
  }

  openWoo(id: string) {
    window.open(
      `https://jackhealthlegacy.flywheelsites.com/wp-admin/user-edit.php?user_id=${id}`,
      '_blank'
    );
  }

  loadPaymentMethods(id: string) {
    this.dispatch(CustomerActions.loadPaymentMethods({ id }));
  }

  /* ------------------------------------------------------------------------ */
  /*                       Customer Appointments Facades                      */
  /* ------------------------------------------------------------------------ */
  loadCustomerAppointments(filters: any) {
    this.dispatch(CustomerActions.loadAppointments({ filters }));
  }

  updateAppointment(id: string, appointment: UpdateAppointmentDTO) {
    this.dispatch(CustomerActions.updateAppointment({ id, appointment }));
  }

  createCustomer(
    customer: NewCustomerDTO,
    lead?: ILead,
    goToCustomer?: boolean
  ) {
    this.dispatch(
      CustomerActions.createCustomer({ customer, lead, goToCustomer })
    );
  }

  openCreateActivity(
    type: ACTIVITY_TYPES,
    customerId: string,
    resourceId?: string,
    resourceType?: string
  ) {
    this.drawerFacade.openDrawer({
      name: DrawerType.CREATE_ACTIVITY,
      props: {
        type,
        customerId
      }
    });
  }

  async createActivity(data: CreateActivityDTO) {
    return await this.apiService.emr.activities
      .create(data)
      .then(res => {
        this.dispatch(CustomerActions.createActivitySuccess({ activity: res }));
        return res;
      })
      .catch(error => {
        this.dispatch(CustomerActions.createActivityFailure({ error }));
        throw error;
      });
  }

  updateCustomerCredit(
    customerId: string,
    amount: number,
    note?: string,
    orderId?: number
  ) {
    this.dispatch(
      CustomerActions.updateCustomerCredit({
        customerId,
        amount,
        note,
        orderId
      })
    );
  }
}
